/** Hard limits enforced by engine health checks (bytes, binary payload). */
export const HEALTH_CHECK_MAX_BYTES = {
    setting: 500 * 1024,
    pdfEmbed: 1800 * 1024,
    emailEmbed: 1800 * 1024,
    storeIcon: 1536 * 1024,
    /**
     * Align with Engine SettingsChecks THEME_MAX_SIZE for mobile theme images
     * (logo, splash, backgroundImage). storeIcon stays PNG-only separately.
     */
    mobileTheme: 1536 * 1024,
};

const IMAGE_TYPE_DEFAULT = 'default';
const IMAGE_TYPE_LOGO = 'logo';
const IMAGE_TYPE_BANNIERE = 'banner';
const IMAGE_TYPE_POSTER = 'poster';
const IMAGE_TYPE_STILL = 'still';
const IMAGE_TYPE_LOGO_A4 = 'logo-a4';
const IMAGE_TYPE_LOGO_THERMAL = 'logo-thermal';
const IMAGE_TYPE_FAVICON = 'favicon';
const IMAGE_TYPE_APPICON = 'appicon';
const IMAGE_TYPE_PARTNERS = 'partners';
const IMAGE_TYPE_PARTNERS_A4 = 'partners-a4';
const IMAGE_TYPE_PARTNERS_THERMAL = 'partners-thermal';

/**
 * Recommended display sizes from product copy (longest side in px).
 * Poster upload longest-side cap (posterUpload / eventPosterUpload) uses
 * SETTING_IMAGE_RECOMMENDED_PX.poster — keep in sync with:
 * - Kore PosterUploadHelper::IMAGE_X / IMAGE_Y
 * - Engine importer/screenings/ozu.js POSTER_MAX_WIDTH / POSTER_MAX_HEIGHT
 */
export const SETTING_IMAGE_RECOMMENDED_PX = {
    [IMAGE_TYPE_DEFAULT]: 1200,
    [IMAGE_TYPE_LOGO]: 300,
    [IMAGE_TYPE_BANNIERE]: 1920,
    [IMAGE_TYPE_POSTER]: 1920,
    [IMAGE_TYPE_STILL]: 1920,
    [IMAGE_TYPE_LOGO_A4]: 800,
    [IMAGE_TYPE_LOGO_THERMAL]: 400,
    [IMAGE_TYPE_FAVICON]: 32,
    [IMAGE_TYPE_APPICON]: 192,
    [IMAGE_TYPE_PARTNERS]: 300,
    [IMAGE_TYPE_PARTNERS_A4]: 800,
    [IMAGE_TYPE_PARTNERS_THERMAL]: 400,
};

export const RETINA_PIXEL_FACTOR = 2;

/** Shared lossy encode quality (0–1). Keep in sync with Kore Image::JPEG_QUALITY. */
export const IMAGE_JPEG_QUALITY = 0.90;

/**
 * Starting WebP quality for poster uploads (0–1). imageProcessor steps down
 * toward MIN only while over maxBytes. Keep starting point in sync with Kore
 * PosterUploadHelper::WEBP_QUALITY when Kore must convert non-WebP sources.
 */
export const IMAGE_WEBP_QUALITY = 0.92;

/** Skip replacement when savings are below this threshold and the file already passes the health check. */
export const NEGLIGIBLE_SAVINGS_BYTES = 20 * 1024;

/** Matches Kore AvatarImageHelper::MAX_BYTES and kuser/application avatar upload. */
export const AVATAR_MAX_BYTES = 12 * 1000 * 1024;

/** Matches Kore avatar processing (image_x / image_y). Multiple of 128 for encode alignment. */
export const AVATAR_MAX_DIMENSION = 256;

const SETTING_THERMAL_IMAGE_TYPES = new Set([
    IMAGE_TYPE_LOGO_THERMAL,
    IMAGE_TYPE_PARTNERS_THERMAL,
]);

const PDF_EMBED_MAX_WIDTH = 2480;
const PDF_EMBED_MAX_HEIGHT = 3508;
const EMAIL_EMBED_MAX_WIDTH = 1510;
/** Longest-side cap (square box; portrait or landscape). See SETTING_IMAGE_RECOMMENDED_PX.poster sync comment. */
const POSTER_UPLOAD_MAX_DIMENSION = SETTING_IMAGE_RECOMMENDED_PX[IMAGE_TYPE_POSTER];

/**
 * @param {'setting'|'pdfEmbed'|'emailEmbed'|'posterUpload'|'eventPosterUpload'|'avatarUpload'|'formImage'|'formStaticImage'|'equipmentCatalog'|'storeIcon'|'mobileTheme'|'mobileThemeLogo'} presetName
 * @param {string|null} imageType
 * @param {object} [overrides]
 */
export function getImagePresetOptions(presetName, imageType = null, overrides = {}) {
    let options;

    switch (presetName) {
        case 'setting': {
            const recommended = SETTING_IMAGE_RECOMMENDED_PX[imageType]
                || SETTING_IMAGE_RECOMMENDED_PX[IMAGE_TYPE_DEFAULT];
            const maxDimension = recommended * RETINA_PIXEL_FACTOR;
            options = {
                maxBytes: HEALTH_CHECK_MAX_BYTES.setting,
                maxWidth: maxDimension,
                maxHeight: maxDimension,
                preserveAlpha: true,
                preservePng: SETTING_THERMAL_IMAGE_TYPES.has(imageType),
                jpegQuality: IMAGE_JPEG_QUALITY,
                negligibleSavingsBytes: NEGLIGIBLE_SAVINGS_BYTES,
            };
            break;
        }
        case 'mobileTheme':
            // Static WebP for in-app RN Image (splash / background).
            // Store builds still flatten splash to PNG frame 0; storeIcon stays PNG.
            options = {
                maxBytes: HEALTH_CHECK_MAX_BYTES.mobileTheme,
                maxWidth: 2400,
                maxHeight: 2400,
                preserveAlpha: true,
                preservePng: false,
                outputFormat: 'webp',
                webpQuality: IMAGE_WEBP_QUALITY,
                jpegQuality: IMAGE_JPEG_QUALITY,
                negligibleSavingsBytes: NEGLIGIBLE_SAVINGS_BYTES,
            };
            break;
        case 'mobileThemeLogo':
            // Public app Logo.js only accepts data:image/svg+xml (SvgXml + primary fill).
            // Never rasterize to WebP — canvas conversion drops fill-less paths to transparent.
            options = {
                maxBytes: HEALTH_CHECK_MAX_BYTES.mobileTheme,
                maxWidth: 2400,
                maxHeight: 2400,
                preserveSvg: true,
                preserveAlpha: true,
                preservePng: true,
                jpegQuality: IMAGE_JPEG_QUALITY,
                negligibleSavingsBytes: NEGLIGIBLE_SAVINGS_BYTES,
            };
            break;
        case 'storeIcon':
            options = {
                maxBytes: HEALTH_CHECK_MAX_BYTES.storeIcon,
                maxWidth: 2048,
                maxHeight: 2048,
                preserveAlpha: false,
                preservePng: true,
                jpegQuality: IMAGE_JPEG_QUALITY,
                negligibleSavingsBytes: NEGLIGIBLE_SAVINGS_BYTES,
            };
            break;
        case 'pdfEmbed':
            options = {
                maxBytes: HEALTH_CHECK_MAX_BYTES.pdfEmbed,
                maxWidth: PDF_EMBED_MAX_WIDTH,
                maxHeight: PDF_EMBED_MAX_HEIGHT,
                preserveAlpha: true,
                preservePng: false,
                jpegQuality: IMAGE_JPEG_QUALITY,
                negligibleSavingsBytes: NEGLIGIBLE_SAVINGS_BYTES,
            };
            break;
        case 'emailEmbed':
            options = {
                maxBytes: HEALTH_CHECK_MAX_BYTES.emailEmbed,
                maxWidth: EMAIL_EMBED_MAX_WIDTH,
                maxHeight: EMAIL_EMBED_MAX_WIDTH,
                preserveAlpha: true,
                preservePng: false,
                jpegQuality: IMAGE_JPEG_QUALITY,
                negligibleSavingsBytes: NEGLIGIBLE_SAVINGS_BYTES,
            };
            break;
        case 'posterUpload':
            options = {
                // Not enforced by a health check, but we still cap to avoid huge base64 payloads.
                // outputFormat webp is also tried opportunistically (jpeg/png → webp) when savings
                // are meaningful; canvas WebP encoding keeps PNG alpha.
                // Square box = longest-side cap (portrait and landscape).
                maxBytes: 1800 * 1024,
                maxWidth: POSTER_UPLOAD_MAX_DIMENSION,
                maxHeight: POSTER_UPLOAD_MAX_DIMENSION,
                preserveAlpha: true,
                preservePng: false,
                outputFormat: 'webp',
                webpQuality: IMAGE_WEBP_QUALITY,
                negligibleSavingsBytes: NEGLIGIBLE_SAVINGS_BYTES,
            };
            break;
        case 'eventPosterUpload':
            options = {
                // Same opportunistic jpeg/png → webp policy as posterUpload.
                maxBytes: 1800 * 1024,
                maxWidth: POSTER_UPLOAD_MAX_DIMENSION,
                maxHeight: POSTER_UPLOAD_MAX_DIMENSION,
                preserveAlpha: true,
                preservePng: false,
                outputFormat: 'webp',
                webpQuality: IMAGE_WEBP_QUALITY,
                negligibleSavingsBytes: NEGLIGIBLE_SAVINGS_BYTES,
            };
            break;
        case 'avatarUpload':
            options = {
                maxBytes: AVATAR_MAX_BYTES,
                maxWidth: AVATAR_MAX_DIMENSION,
                maxHeight: AVATAR_MAX_DIMENSION,
                preserveAlpha: false,
                preservePng: false,
                jpegQuality: IMAGE_JPEG_QUALITY,
                forceProcess: true,
                negligibleSavingsBytes: 0,
            };
            break;
        case 'formImage':
            options = {
                maxBytes: 2 * 1024 * 1024,
                maxWidth: 1920,
                maxHeight: 1920,
                preserveAlpha: false,
                preservePng: false,
                jpegQuality: IMAGE_JPEG_QUALITY,
                forceProcess: true,
                negligibleSavingsBytes: 0,
            };
            break;
        case 'formStaticImage':
            // Display-only images in web forms (not PDF). Prefer WebP, keep alpha.
            options = {
                maxBytes: 1800 * 1024,
                maxWidth: 1920,
                maxHeight: 1920,
                preserveAlpha: true,
                preservePng: false,
                outputFormat: 'webp',
                webpQuality: IMAGE_WEBP_QUALITY,
                jpegQuality: IMAGE_JPEG_QUALITY,
                forceProcess: true,
                negligibleSavingsBytes: 0,
            };
            break;
        case 'equipmentCatalog':
            // Keep in sync with kore/dev/scripts/equipment_catalog/sync_equipment_catalog.py
            // (--max-width 600, --quality 80) and catalog.json source photos.
            options = {
                maxBytes: 500 * 1024,
                maxWidth: 600,
                maxHeight: 600,
                preserveAlpha: true,
                preservePng: false,
                outputFormat: 'webp',
                webpQuality: 0.80,
                forceProcess: true,
                negligibleSavingsBytes: 0,
            };
            break;
        default:
            throw new Error(`Unknown image preset: ${presetName}`);
    }

    return { ...options, ...overrides };
}
